import { gql, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCurrentUserProfile } from 'src/graphql';
import { deepCloneAndOmit } from 'src/utils/objects';
import { MacroConfig } from './macro-config.types';

const GET_MACRO_CONFIG_QUERY = gql`
  query GetMacroConfig {
    getMacroConfig {
      version
      actionTypes {
        label
        value
      }
      actions {
        data {
          group
          attribute
          attributeLabel
          uiType
          options {
            value
            label
          }
          optionsQuery {
            graphqlQuery
          }
        }
        manageConversationLabels {
          attributes {
            name
            uiType
            label
            multiple
            optionsQuery {
              graphqlQuery
            }
            options {
              value
              label
            }
          }
        }
        manageSubAssignees {
          attributes {
            name
            uiType
            label
            multiple
            optionsQuery {
              graphqlQuery
            }
            options {
              value
              label
            }
          }
        }
        signal {
          attributes {
            name
            uiType
            label
            options {
              value
              label
            }
            dependsOn {
              attribute
              value
            }
          }
        }
      }
    }
  }
`;

interface GetMacroConfigQuery {
  getMacroConfig: MacroConfig & { __typename: string };
}

export const useMacroConfig = () => {
  const { user } = useCurrentUserProfile();
  const { data: macroConfigData, loading: macroConfigLoading } = useQuery<GetMacroConfigQuery>(
    GET_MACRO_CONFIG_QUERY,
    {
      context: {
        includePlatformLevelData: user?.partnerId === PLATFORM_PARTNER_ID,
      },
    },
  );

  const configWithoutTypenameKeys = useMemo(
    () =>
      macroConfigData?.getMacroConfig
        ? deepCloneAndOmit<GetMacroConfigQuery['getMacroConfig']>(
            macroConfigData?.getMacroConfig,
            ['__typename'],
          )
        : null,
    [macroConfigData?.getMacroConfig],
  );

  return {
    macroConfig: configWithoutTypenameKeys,
    loading: macroConfigLoading,
  };
};
